import { Button, Card, HeartCalendarIcon } from "@nfp-tracker/components";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@nfp-tracker/components";
import { ROUTES } from "../constants/routes";
import { needsOnboarding } from "../integrations/supabase/domain";
import { supabase } from "../integrations/supabase/client";
import { buildAuthRedirectUrl } from "../utils/authRedirect";

// Confirmation links are single-use. Desktop mail clients and link scanners often
// fetch them before the recipient does, so an expired/consumed link is a normal
// outcome we have to explain rather than sit on.
const AUTH_CALLBACK_ERROR_COPY: Record<string, string> = {
  otp_expired:
    "That confirmation link has expired or was already used. Send yourself a fresh one below.",
  access_denied:
    "That confirmation link is no longer valid. Send yourself a fresh one below.",
};

const readErrorMessage = (error: unknown, fallback: string) =>
  (typeof error === "object" && error && "message" in error
    ? (error as { message?: string }).message
    : null) || fallback;

const describeAuthCallbackError = (code: string, description: string | null) => {
  const known = AUTH_CALLBACK_ERROR_COPY[code];
  if (known) {
    return known;
  }

  const readable = description?.replace(/\+/g, " ").trim();
  return readable
    ? `We couldn't confirm your email: ${readable}`
    : "We couldn't confirm your email with that link. Send yourself a fresh one below.";
};

// A PKCE code exchange needs the verifier stored by the browser that started
// signup. Opening the link somewhere else still confirms the email server-side,
// it just can't create the session here.
const describeExchangeError = (message: string) =>
  /code.?verifier|code.?challenge|invalid request/i.test(message)
    ? "Your email is confirmed, but we couldn't sign you in automatically from this browser. Please sign in to finish setup."
    : message;

const SignupConfirmEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showManualContinue, setShowManualContinue] = useState(false);
  const [isManualContinuing, setIsManualContinuing] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  // Mirrors isRedirecting so the poll timer and auth listener can read it
  // without re-running the effect that owns them.
  const isRedirectingRef = useRef(false);

  const email = useMemo(() => searchParams.get("email")?.trim().toLowerCase() || "", [searchParams]);
  const inviteToken = useMemo(() => searchParams.get("inviteToken")?.trim() || "", [searchParams]);

  const onboardingPath = useMemo(() => {
    if (!inviteToken) {
      return ROUTES.onboarding;
    }

    return `${ROUTES.onboarding}?inviteToken=${encodeURIComponent(inviteToken)}`;
  }, [inviteToken]);

  const loginPath = useMemo(() => {
    const params = new URLSearchParams();
    if (email) {
      params.set("email", email);
    }
    if (inviteToken) {
      params.set("inviteToken", inviteToken);
    }

    const query = params.toString();
    return query ? `${ROUTES.login}?${query}` : ROUTES.login;
  }, [email, inviteToken]);

  const buildConfirmRedirect = useCallback(() => {
    if (!inviteToken) {
      return buildAuthRedirectUrl(ROUTES.signupConfirmEmail);
    }

    return buildAuthRedirectUrl(
      `${ROUTES.signupConfirmEmail}?inviteToken=${encodeURIComponent(inviteToken)}`
    );
  }, [inviteToken]);

  const routeConfirmedUser = useCallback(async (userId: string) => {
    if (isRedirectingRef.current) {
      return;
    }

    isRedirectingRef.current = true;
    setIsRedirecting(true);

    try {
      if (inviteToken) {
        navigate(onboardingPath, { replace: true });
        return;
      }

      const mustOnboard = await needsOnboarding(userId);
      navigate(mustOnboard ? ROUTES.onboarding : ROUTES.observations, { replace: true });
    } catch (error) {
      // Release the latch so a retry (poll, auth event, Continue) can route again.
      isRedirectingRef.current = false;
      setIsRedirecting(false);
      throw error;
    }
  }, [inviteToken, navigate, onboardingPath]);

  const getConfirmedUser = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    return user?.email_confirmed_at ? user : null;
  }, []);

  const checkForConfirmedUser = useCallback(async () => {
    const user = await getConfirmedUser();

    if (!user) {
      setIsChecking(false);
      return false;
    }

    await routeConfirmedUser(user.id);
    return true;
  }, [getConfirmedUser, routeConfirmedUser]);

  const cleanupAuthCallbackUrl = useCallback(() => {
    const current = new URL(window.location.href);
    const keep = new URLSearchParams();
    const preservedEmail = current.searchParams.get("email");
    const preservedInviteToken = current.searchParams.get("inviteToken");

    if (preservedEmail) {
      keep.set("email", preservedEmail);
    }

    if (preservedInviteToken) {
      keep.set("inviteToken", preservedInviteToken);
    }

    const query = keep.toString();
    const nextUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;

    window.history.replaceState(null, "", nextUrl);
  }, []);

  const consumeAuthCallbackIfPresent = useCallback(async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

    const authCode = queryParams.get("code");
    const tokenHash = queryParams.get("token_hash") || hashParams.get("token_hash");
    const callbackType = queryParams.get("type") || hashParams.get("type");
    const accessToken = hashParams.get("access_token") || queryParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token") || queryParams.get("refresh_token");
    const errorCode =
      queryParams.get("error_code") ||
      hashParams.get("error_code") ||
      queryParams.get("error") ||
      hashParams.get("error");
    const errorDescription =
      queryParams.get("error_description") || hashParams.get("error_description");

    if (errorCode) {
      // Strip the failed callback so retries don't replay the same dead link.
      cleanupAuthCallbackUrl();
      throw new Error(describeAuthCallbackError(errorCode, errorDescription));
    }

    if (!authCode && !(tokenHash && callbackType) && !(accessToken && refreshToken)) {
      return;
    }

    if (await getConfirmedUser()) {
      cleanupAuthCallbackUrl();
      return;
    }

    let consumed = false;

    if (authCode) {
      const { error } = await supabase.auth.exchangeCodeForSession(authCode);
      if (error) {
        // The code is spent either way — don't leave it in the URL to retry.
        cleanupAuthCallbackUrl();
        throw new Error(describeExchangeError(error.message));
      }
      consumed = true;
    } else if (tokenHash && callbackType) {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: callbackType as "signup" | "recovery" | "invite" | "email_change" | "email" | "magiclink",
      });
      if (error) {
        cleanupAuthCallbackUrl();
        throw error;
      }
      consumed = true;
    } else if (accessToken && refreshToken) {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) {
        cleanupAuthCallbackUrl();
        throw error;
      }
      consumed = true;
    }

    if (consumed) {
      cleanupAuthCallbackUrl();
    }
  }, [cleanupAuthCallbackUrl, getConfirmedUser]);

  useEffect(() => {
    let isMounted = true;

    const initialCheck = async () => {
      setIsChecking(true);
      setShowManualContinue(false);
      setLinkError(null);
      let callbackErrorMessage: string | null = null;
      let didRoute = false;

      try {
        await consumeAuthCallbackIfPresent();
      } catch (error: unknown) {
        callbackErrorMessage = readErrorMessage(
          error,
          "We couldn't finish email confirmation automatically. Please try signing in."
        );
      }

      // Must not escape: an unhandled rejection here used to leave the page
      // stuck on the spinner with no way out.
      try {
        didRoute = await checkForConfirmedUser();
      } catch (error: unknown) {
        didRoute = false;
        callbackErrorMessage =
          callbackErrorMessage ||
          readErrorMessage(
            error,
            "We couldn't check your confirmation status. Please try again or sign in."
          );
      }

      if (!isMounted || didRoute) {
        return;
      }

      if (callbackErrorMessage) {
        setLinkError(callbackErrorMessage);
        toast({
          title: "Confirmation link issue",
          description: callbackErrorMessage,
          variant: "destructive",
        });
      }

      setIsChecking(false);
      setShowManualContinue(true);
    };

    void initialCheck();

    // Fallback polling catches confirmations that happen in another tab/device
    // where auth state events may not fire in this tab. Bounded so an
    // abandoned tab stops hitting the auth endpoint forever.
    let pollAttempts = 0;
    const maxPollAttempts = 75; // ~5 minutes at 4s
    const pollTimer = window.setInterval(() => {
      if (isRedirectingRef.current) {
        return;
      }

      pollAttempts += 1;
      if (pollAttempts > maxPollAttempts) {
        window.clearInterval(pollTimer);
        if (isMounted) {
          setIsChecking(false);
          setShowManualContinue(true);
        }
        return;
      }

      void checkForConfirmedUser().catch(() => {
        // Transient failure — the next tick or Continue will retry.
      });
    }, 4000);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted || !session || isRedirectingRef.current) {
        return;
      }

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        try {
          await checkForConfirmedUser();
        } catch {
          // Recovery is handled by the poll and the Continue button.
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      window.clearInterval(pollTimer);
    };
  }, [
    checkForConfirmedUser,
    consumeAuthCallbackIfPresent,
    location.hash,
    location.search,
    toast,
  ]);

  useEffect(() => {
    if (isRedirecting || isChecking) {
      return;
    }

    const revealTimer = window.setTimeout(() => {
      setShowManualContinue(true);
    }, 8000);

    return () => {
      window.clearTimeout(revealTimer);
    };
  }, [isChecking, isRedirecting]);

  // Last-resort watchdog. Deliberately ungated: if anything stalls, the user
  // always gets a Continue button instead of an endless spinner. Cleared on
  // unmount, so a successful redirect never sees it.
  useEffect(() => {
    const watchdog = window.setTimeout(() => {
      setIsChecking(false);
      setShowManualContinue(true);
    }, 12000);

    return () => {
      window.clearTimeout(watchdog);
    };
  }, []);

  const handleResend = async () => {
    if (!email) {
      toast({
        title: "Missing email",
        description: "Return to sign up and try again so we know where to resend.",
        variant: "destructive",
      });
      return;
    }

    setIsResending(true);

    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: buildConfirmRedirect(),
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Email sent",
        description: "We sent a fresh confirmation link.",
      });
    } catch (error: unknown) {
      const message =
        typeof error === "object" && error && "message" in error
          ? (error as { message?: string }).message
          : "We couldn't resend the confirmation email. Please try again.";

      toast({
        title: "Something didn't work — let's try that again",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleManualContinue = async () => {
    setIsManualContinuing(true);
    setLinkError(null);
    const fallbackMessage =
      "We couldn't continue automatically. Please sign in to finish setup.";
    let message: string | null = null;

    try {
      await consumeAuthCallbackIfPresent();
    } catch (error: unknown) {
      message = readErrorMessage(error, fallbackMessage);
    }

    let didRoute = false;
    try {
      didRoute = await checkForConfirmedUser();
    } catch (error: unknown) {
      message = message || readErrorMessage(error, fallbackMessage);
    }

    setIsManualContinuing(false);

    if (didRoute) {
      return;
    }

    if (message) {
      setLinkError(message);
      toast({
        title: "Continue check failed",
        description: message,
        variant: "destructive",
      });
      return;
    }

    navigate(loginPath, { replace: true });
  };

  return (
    <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted relative">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-accent blur-3xl animate-pulse [animation-delay:1s]" />
      </div>

      <Card className="compact-density-mobile w-full max-w-md p-8 space-y-6 shadow-[var(--shadow-medium)] animate-fade-in relative z-10">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-6">
            <div className="relative overflow-hidden rounded-[28px]">
              <HeartCalendarIcon className="w-32 h-32 object-cover" />
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Check your email</h1>
          <p className="text-muted-foreground">
            {email
              ? `We sent a confirmation link to ${email}.`
              : "We sent a confirmation link to your email address."}
          </p>
          <p className="text-muted-foreground">
            Email links usually open in a different tab or app. After confirming there, return here and tap Continue.
          </p>
        </div>

        {linkError && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2">
            {linkError}
          </div>
        )}

        {(isChecking || isRedirecting) && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Checking for a completed confirmation...</span>
          </div>
        )}

        <div className="space-y-3">
          {showManualContinue && (
            <Button
              type="button"
              className="w-full h-12 text-base"
              onClick={handleManualContinue}
              disabled={isManualContinuing}
            >
              {isManualContinuing ? "Continuing..." : "Continue"}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 text-base"
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? "Sending..." : "Resend confirmation email"}
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          If confirmation opened elsewhere, come back here and press Continue.
        </p>

        <div className="text-center text-sm">
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => navigate(ROUTES.login)}
          >
            Already confirmed? Sign in
          </button>
        </div>
      </Card>
    </div>
  );
};

export default SignupConfirmEmail;
